/**
 * API Client - Centralized HTTP client with error handling
 *
 * Features:
 * - Automatic token injection
 * - Request/response interceptors
 * - Centralized error handling
 * - Automatic retry logic for failed requests
 * - Request/response logging in development
 * - Configurable timeouts per request
 *
 * Timeout Usage:
 * - Default timeout: 30 seconds (for most API calls)
 * - Long timeout: 5 minutes (exported as LONG_TIMEOUT)
 * - To use long timeout: api.post(url, data, { timeout: LONG_TIMEOUT })
 * - Example: Document import, large file uploads, complex AI operations
 */

import axios from 'axios';
import toast from 'react-hot-toast';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE;
const API_TIMEOUT = 30000; // 30 seconds (default)
const LONG_TIMEOUT = 300000; // 5 minutes (for long-running operations)

/**
 * Custom API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * - Adds authorization token to requests
 * - Logs requests in development mode
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if it exists (check both storages)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    // Log request error
    if (import.meta.env.DEV) {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * - Handles successful responses
 * - Catches and transforms errors
 * - Handles token expiration
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    // Check if this is a cancelled request (from AbortController)
    if (axios.isCancel(error)) {
      // Don't show toast for cancelled requests
      if (import.meta.env.DEV) {
        console.log('[API] Request cancelled:', error.config?.url);
      }
      throw error;
    }

    // Extract error details
    const status = error.response?.status;
    const data = error.response?.data;
    const message = data?.detail || data?.message || error.message || 'An unexpected error occurred';

    // Log error in development
    if (import.meta.env.DEV) {
      console.error('[API Error]', {
        status,
        message,
        data,
        url: error.config?.url,
      });
    }

    // Handle specific error cases
    switch (status) {
      case 401:
        // Check if this is a login attempt failure vs expired token
        const isLoginAttempt = error.config?.url?.includes('/api/v1/auth/login');
        const isRegisterAttempt = error.config?.url?.includes('/api/v1/auth/register');

        if (isLoginAttempt) {
          // Failed login attempt - show error but don't redirect
          toast.error('Invalid username or password');
        } else if (isRegisterAttempt) {
          // Failed registration - let the specific error message show
          toast.error(message);
        } else {
          // Expired/invalid token - clear auth and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
            toast.error('Session expired. Please login again.');
          }
        }
        break;

      case 403:
        toast.error('You do not have permission to perform this action.');
        break;

      case 404:
        toast.error('The requested resource was not found.');
        break;

      case 422:
        // Validation error
        const validationMessage = data?.detail?.[0]?.msg || message;
        toast.error(`Validation Error: ${validationMessage}`);
        break;

      case 500:
        toast.error('Server error. Please try again later.');
        break;

      case 503:
        toast.error('Service temporarily unavailable. Please try again later.');
        break;

      default:
        // Network error or other issues
        if (!error.response) {
          toast.error('Network error. Please check your connection.');
        }
    }

    // Create and throw custom API error
    throw new ApiError(message, status, data);
  }
);

/**
 * API Service Methods
 * Higher-level wrapper functions for common HTTP operations
 */
export const api = {
  /**
   * GET request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config options
   * @returns {Promise} Response data
   */
  async get(url, config = {}) {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * POST request
   * @param {string} url - API endpoint
   * @param {object} data - Request payload
   * @param {object} config - Axios config options
   * @returns {Promise} Response data
   */
  async post(url, data = {}, config = {}) {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PUT request
   * @param {string} url - API endpoint
   * @param {object} data - Request payload
   * @param {object} config - Axios config options
   * @returns {Promise} Response data
   */
  async put(url, data = {}, config = {}) {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PATCH request
   * @param {string} url - API endpoint
   * @param {object} data - Request payload
   * @param {object} config - Axios config options
   * @returns {Promise} Response data
   */
  async patch(url, data = {}, config = {}) {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * DELETE request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config options
   * @returns {Promise} Response data
   */
  async delete(url, config = {}) {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Export the axios instance for advanced usage
export default apiClient;

// Export timeout constants for use in specific API calls
export { LONG_TIMEOUT };
